/**
 * MandateEditor - Component for editing political mandates
 */

import { useState } from 'react';
import {
  PoliticalMandate,
  BrazilianPoliticalPosition,
  BRAZILIAN_POLITICAL_POSITIONS,
} from '@/types/tenant.types';

interface MandateEditorProps {
  mandates: PoliticalMandate[];
  onChange: (mandates: PoliticalMandate[]) => void;
  disabled?: boolean;
  maxMandates?: number;
}

interface NewMandateForm {
  position: BrazilianPoliticalPosition;
  year: string;
  location: string;
}

const currentYear = new Date().getFullYear();

export function MandateEditor({
  mandates,
  onChange,
  disabled,
  maxMandates = 20,
}: MandateEditorProps) {
  const [newMandate, setNewMandate] = useState<NewMandateForm>({
    position: 'Vereador',
    year: '',
    location: '',
  });
  const [error, setError] = useState<string | null>(null);

  const addMandate = () => {
    setError(null);

    // Validate year
    const year = parseInt(newMandate.year, 10);
    if (isNaN(year) || year < 1900 || year > currentYear + 4) {
      setError(`Ano deve estar entre 1900 e ${currentYear + 4}`);
      return;
    }

    // Validate location
    if (!newMandate.location.trim()) {
      setError('Localidade é obrigatória');
      return;
    }

    // Check max mandates
    if (mandates.length >= maxMandates) {
      setError(`Máximo de ${maxMandates} mandatos permitidos`);
      return;
    }

    const mandate: PoliticalMandate = {
      position: newMandate.position,
      year,
      location: newMandate.location.trim(),
    };

    onChange([...mandates, mandate]);

    // Reset form
    setNewMandate({
      position: 'Vereador',
      year: '',
      location: '',
    });
  };

  const removeMandate = (index: number) => {
    onChange(mandates.filter((_, i) => i !== index));
  };

  const formatMandate = (mandate: PoliticalMandate): string => {
    return `${mandate.position} - ${mandate.location} - ${mandate.year}`;
  };

  return (
    <div className="space-y-4">
      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* List of mandates */}
      {mandates.length > 0 && (
        <ul className="space-y-2">
          {mandates.map((mandate, index) => (
            <li
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md group"
            >
              <div className="flex items-center gap-2">
                <span className="text-blue-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                  </svg>
                </span>
                <span className="text-sm text-gray-700">{formatMandate(mandate)}</span>
              </div>
              <button
                type="button"
                onClick={() => removeMandate(index)}
                disabled={disabled}
                className="text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add new mandate form */}
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Position dropdown */}
        <select
          value={newMandate.position}
          onChange={(e) =>
            setNewMandate({
              ...newMandate,
              position: e.target.value as BrazilianPoliticalPosition,
            })
          }
          disabled={disabled}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        >
          {BRAZILIAN_POLITICAL_POSITIONS.map((position) => (
            <option key={position} value={position}>
              {position}
            </option>
          ))}
        </select>

        {/* Year input */}
        <input
          type="number"
          value={newMandate.year}
          onChange={(e) => setNewMandate({ ...newMandate, year: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && addMandate()}
          disabled={disabled}
          placeholder="Ano"
          min={1900}
          max={currentYear + 4}
          className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        />

        {/* Location input */}
        <input
          type="text"
          value={newMandate.location}
          onChange={(e) => setNewMandate({ ...newMandate, location: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && addMandate()}
          disabled={disabled}
          placeholder="Cidade/Estado"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        />

        {/* Add button */}
        <button
          type="button"
          onClick={addMandate}
          disabled={disabled || !newMandate.year || !newMandate.location.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          Adicionar
        </button>
      </div>

      {/* Helper text */}
      <p className="text-xs text-gray-500">Ex: Vereador - São Paulo/SP - 2020</p>
    </div>
  );
}
